import * as _ from 'lodash';
import * as qs from 'qs';
import { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { browserHistory } from 'react-router';
import type { VoidFunctionComponent } from 'react';

import updateUserPermissions from '../../../../../../helpers/updateUserPermissions';
import { useOnMount } from '../../../../../../hooks';
import { nuc } from '../../../../../../utils';
import { shouldDisplayOrganizationPortalTokenSettings } from '../../../../../settings/shared/permissions';
import { useTeamsSelectOptions } from '../../../../../shared/teams/hooks';
import { commitTokenChanges } from '../../../request';
import useCreateGroup from '../../hooks/useCreateGroup';
import { useFetchMinimalProjectsAndUpdateState } from '../../hooks/useMinimalProjects';
import usePoliciesSelection from '../../hooks/usePoliciesSelection';
import usePortalSettingsState from '../../hooks/usePortalSettingsState';
import useReleaseGroupState from '../../hooks/useReleaseGroupState';
import ErrorBanner from '../../shared/ErrorBanner';
import ProjectsPage from '../../shared/ProjectsPage/ProjectsPage';
import type { ProjectsFilter } from '../../../../../../../shared/projectsV2';
import type { CreateGroupRequest, NewReleaseProject } from '../../../../../../../shared/releaseGroups';
import type { RouterProps } from '../../../../../../types';

import ReleaseGroupsCTA from './ReleaseGroupsCTA/ReleaseGroupsCTA';

import styles from '../../shared/ReleaseGroupsRevamp.module.less';

export type Props = RouterProps;

const CreateGroupContainer: VoidFunctionComponent<Props> = ({ location }) => {
  const state = useReleaseGroupState();
  const { state: portalSettingsState, actions: portalSettingActions } = usePortalSettingsState();

  const { onFetchMinimalProjectsAndAdd, onAddAllProjects, onRemoveAllProjects } =
    useFetchMinimalProjectsAndUpdateState(state.addMinimalProjects, state.removeProjects, state.filters);

  const policiesSelection = usePoliciesSelection();

  useOnMount(async () => {
    // Check the incoming URL query for initially-added projects
    // (selected from the ProjectsV2 Dashboard).
    const query = qs.parse(location.search ?? '', { ignoreQueryPrefix: true });

    // Split the URL params into selected projects and filters.
    const selected = query.selected as 'all' | string[] | undefined;
    const filters = _.omit(query ?? {}, 'selected') as ProjectsFilter;

    // Clear out the URL's search params after hydration.
    window.history.replaceState({}, '', location.pathname);

    // If nothing is selected, then we are creating a new release group from scratch.
    if (!selected) {
      // When creating a new release group from scratch, show ALL projects: not just added ones.
      state.setShouldShowOnlyAddedProjects(false);
      return;
    }

    // If "all" projects are selected, hydrate added projects via filters.
    if (selected === 'all') {
      const fetchedProjects = await onFetchMinimalProjectsAndAdd(filters);

      // If there are no projects to add, return early.
      if (_.isNil(fetchedProjects.data) || _.isEmpty(fetchedProjects.data.projects)) {
        return;
      }

      // Since the user is adding projects via filters, apply them to the projects table.
      // Since we should only show added projects, update the project filters to re-fetch
      // only the ones matching the incoming locators. If the user decides that they want to
      // see _all_ projects again, they can toggle the "Show only added projects" button.
      const locators = fetchedProjects.data.projects.map((project) => project.locator);
      state.setFilters({ locators });
      return;
    }

    // If individual projects are selected, hydrate added projects via locators.
    if (!_.isEmpty(selected)) {
      const locators = selected.map(nuc);
      onFetchMinimalProjectsAndAdd({ locators });
      // Since we should only show added projects, update the project filters to re-fetch
      // only the ones matching the incoming locators. If the user decides that they want to
      // see _all_ projects again, they can toggle the "Show only added projects" button.
      state.setFilters({ locators: selected });
    }
  });

  const {
    onCreateGroup,
    onResetErrors,
    createGroupResponse,
    createGroupErrors,
    isCreating,
  } = useCreateGroup();

  // Fetch the list of teams the user has access to for use on the policies page.
  const { teamOptions, selectedTeamOptions, onTeamsSelected } = useTeamsSelectOptions();

  const onCancel = () => {
    // TODO (CORE-3380): This is the most straightforward way to accomplish this.
    // However, it takes the browser a few seconds to pre-load all the data required
    // to render the ProjectsV2 dashboard. Can we make this UX better somehow?
    browserHistory.push('/projects');
  };
  const onSave = async () => {
    // Construct the list of projects to be added based on the ones
    // present in the "addedProjects" state.
    const projects: NewReleaseProject[] = [];
    Object.values(state.addedProjects).forEach((project) => {
      // If this project has no revision selected, skip it.
      if (!project.revision) {
        return;
      }

      const newReleaseProject: NewReleaseProject = {
        projectId: project.locator,
        branch: project.branch,
        revisionId: project.revision.locator,
      };
      projects.push(newReleaseProject);
    });

    // Construct and submit the request to create a new release group.
    const createGroupRequest: CreateGroupRequest = {
      title: state.name,
      release: {
        title: state.version,
        projects,
      },
      teams: selectedTeamOptions.length > 0
        ? selectedTeamOptions.map((team) => team.value)
        : undefined,
      licensingPolicyId: policiesSelection.selectedPolicies.licensing.value,
      securityPolicyId: policiesSelection.selectedPolicies.vulnerability.value,
      qualityPolicyId: policiesSelection.selectedPolicies.quality.value,
      publicOnPortal: portalSettingsState.publicOnPortal,
    };

    const releaseGroup = await onCreateGroup(createGroupRequest);
    if (releaseGroup?.data?.id) {
      await commitTokenChanges(
        releaseGroup.data.id,
        portalSettingsState.pendingAddTokenIds,
        portalSettingsState.pendingRemoveTokenIds,
      );
    }
  };

  const handleGroupCreated = useCallback(async (releaseGroupId) => {
    // If the creation succeeds, redirect the user to the newly-created group.
    await updateUserPermissions();
    browserHistory.push(`/projects/group/${releaseGroupId}`);
  }, []);

  // Handle the effects of group creation: If it succeeds, redirect the user to the newly-created group.
  useEffect(() => {
    if (createGroupResponse) {
      handleGroupCreated(createGroupResponse.id);
    }
  }, [createGroupErrors, createGroupResponse, handleGroupCreated]);

  return (
    <>
      <Helmet title="Create Release Group | FOSSA" />
      <div className={styles.cta}>
        <ReleaseGroupsCTA titleText="New Release Group" />
      </div>
      {createGroupErrors && (
        <div className={styles.banner}>
          <ErrorBanner
            title="An error occurred while creating the release group."
            body={createGroupErrors.join('. ')}
            onDismiss={() => onResetErrors()}
          />
        </div>
      )}
      <ProjectsPage>
        <ProjectsPage.Header
          pageTitle="Create release group"
          saveButtonText="Create"
          validationErrors={state.validate({ isCreatingGroup: true })}
          onCancel={onCancel}
          onSave={onSave}
          isLoading={isCreating}
        />
        <ProjectsPage.Details
          title="Details"
          description="Give your release a name and version number"
          name={state.name}
          version={state.version}
          setName={state.setName}
          setVersion={state.setVersion}
          isNameVisible
          isTeamSelectorVisible
          teamOptions={teamOptions}
          selectedTeamOptions={selectedTeamOptions}
          onTeamsSelected={onTeamsSelected}
        />
        {
          /*
            The `ProjectsPage.PortalTokens` component is used for managing portal token settings.
            These settings are meant to apply to the entire release group, not individual releases.
            Therefore, this component is only included in the `CreateGroupContainer` component for creating a new release group,
            and not in the `EditReleaseContainer` or `CloneReleaseContainer` components for editing or cloning a release.
          */
        }
        {shouldDisplayOrganizationPortalTokenSettings() && <ProjectsPage.PortalTokens
          onAddToken={portalSettingActions.onAddToken}
          onRemoveToken={portalSettingActions.onRemoveToken}
          publicOnPortal={portalSettingsState.publicOnPortal}
          setPublicOnPortal={portalSettingActions.setPublicOnPortal}
        />}
        <ProjectsPage.Policies
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...policiesSelection}
        />
        <ProjectsPage.Projects
          shouldShowOnlyAddedProjects={state.shouldShowOnlyAddedProjects}
          setShouldShowOnlyAddedProjects={state.setShouldShowOnlyAddedProjects}
          areProjectsLoading={state.areProjectsLoading}
          projectCount={state.projectCount}
          projects={state.projects}
          projectErrors={state.projectErrors}
          addedProjects={state.addedProjects}
          onUpdateAddedProject={state.updateAddedProject}
          onAddProject={state.addProjects}
          onRemoveProject={state.removeProjects}
          onAddAllProjects={onAddAllProjects}
          onRemoveAllProjects={onRemoveAllProjects}
          setFilters={state.setFilters}
          filters={state.filters}
          onSelectLatestRevisionForSelectedProjects={state.selectLatestRevisionForSelectedProjects}
          sort={state.sort}
          setSort={state.setSort}
          page={state.page}
          setPage={state.setPage}
          count={state.count}
          setCount={state.setCount}
        />
        <ProjectsPage.Footer
          saveButtonText="Create"
          validationErrors={state.validate()}
          onCancel={onCancel}
          isLoading={isCreating}
          onSave={onSave}
        />
      </ProjectsPage>
    </>
  );
};

export default CreateGroupContainer;
